/**
 * TruBeauté — Affirming Resources page (/resources).
 *
 * What this page is
 * ─────────────────
 * A curated international directory of gender-affirming organizations:
 * healthcare, financial assistance, legal support, crisis lines,
 * community spaces, employment, voice training. First-class mission
 * page — not a footer afterthought.
 *
 * Filtering model
 *   • Country chip (all + 10 countries) — single-select
 *   • Category chip (all + 8 categories) — single-select
 *   • Free-text search — matches against org name + locale-resolved
 *     description (so a French user typing "santé" finds Rainbow
 *     Health Ontario via its French desc, while an English user
 *     typing "health" finds the same row via its English desc)
 *
 * Layout
 *   Resources are grouped by country with a flag heading. Within
 *   each country we sort alphabetically by name. Empty country
 *   groups (after filtering) are hidden so the page never shows
 *   awkward dead sections.
 *
 * i18n
 *   All UI labels go through t("resources.*"). Organization names
 *   are NEVER translated (the org owns its identity). Descriptions
 *   are pulled from the data file's per-locale `description.{lang}`
 *   with EN fallback.
 *
 * SEO
 *   document.title + meta description updated on mount for crawlers
 *   that render JS, and we leave the static <title> in index.html
 *   alone (it's a fine default for non-JS crawlers).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Heart,
  Search,
  ExternalLink,
  Sparkles,
} from "lucide-react";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import {
  RESOURCES,
  COUNTRY_CODES,
  CATEGORY_CODES,
  COUNTRY_FLAGS,
  resolveDescription,
} from "../data/affirmingResources";

const SEO_TITLE_BY_LOCALE = {
  en: "Affirming Resources | TruBeauté",
  fr: "Ressources d'affirmation | TruBeauté",
  es: "Recursos afirmativos | TruBeauté",
  pt: "Recursos afirmativos | TruBeauté",
};

const ResourceCard = ({ resource, descriptionText, categoryLabel }) => (
  <a
    href={resource.url}
    target="_blank"
    rel="noopener noreferrer"
    className="group block rounded-2xl border border-[#EADDD7] bg-white p-5 hover:border-[#D5A8A4] hover:shadow-[0_8px_24px_rgba(155,106,108,0.12)] hover:-translate-y-0.5 transition-all"
    data-testid={`resource-card-${resource.id}`}
  >
    <div className="flex items-start justify-between gap-3 mb-2">
      <span className="inline-block text-[10px] uppercase tracking-[0.2em] text-[#9B6A6C] font-medium">
        {categoryLabel}
      </span>
      <ExternalLink className="h-4 w-4 text-[#C4A6A7] group-hover:text-[#9B6A6C] transition-colors shrink-0 mt-0.5" />
    </div>
    <h3 className="text-lg font-serif text-[#2C2123] mb-2 leading-snug">
      {resource.name}
    </h3>
    <p className="text-sm text-[#5C4B51] leading-relaxed">{descriptionText}</p>
  </a>
);

const FilterChip = ({ active, label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    className={
      active
        ? "px-4 py-2 rounded-full text-sm font-medium bg-[#9B6A6C] text-white shadow-[0_2px_8px_rgba(155,106,108,0.3)] transition-all"
        : "px-4 py-2 rounded-full text-sm bg-white text-[#5C4B51] border border-[#EADDD7] hover:border-[#D5A8A4] hover:text-[#9B6A6C] transition-all"
    }
    data-testid={testId}
    aria-pressed={active}
  >
    {label}
  </button>
);

const AffirmingResources = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = (i18n.language || "en").split("-")[0];

  const [country, setCountry] = useState("all");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");

  // SEO — set on mount + every locale change. We also reset on unmount
  // so the title doesn't bleed into the next page the user navigates
  // to (which would be a subtle bug for the meta-description tag too).
  useEffect(() => {
    const prevTitle = document.title;
    document.title = SEO_TITLE_BY_LOCALE[locale] || SEO_TITLE_BY_LOCALE.en;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", t("resources.seo.metaDescription"));
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, [locale, t]);

  // Locale-aware filtering. Normalize query to handle case/diacritics
  // softly — we do a simple lowercase compare since strict NFD
  // folding is overkill for a directory of ~45 rows.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return RESOURCES.filter((r) => {
      if (country !== "all" && r.country !== country) return false;
      if (category !== "all" && r.category !== category) return false;
      if (q) {
        const haystack = `${r.name} ${resolveDescription(r, locale)}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [country, category, query, locale]);

  // Group results by country (in COUNTRY_CODES display order) and
  // sort alphabetically by name within each country.
  const grouped = useMemo(() => {
    const byCountry = new Map();
    for (const r of filtered) {
      if (!byCountry.has(r.country)) byCountry.set(r.country, []);
      byCountry.get(r.country).push(r);
    }
    return COUNTRY_CODES
      .map((c) => ({
        country: c,
        rows: (byCountry.get(c) || []).slice().sort((a, b) =>
          a.name.localeCompare(b.name, locale),
        ),
      }))
      .filter((g) => g.rows.length > 0);
  }, [filtered, locale]);

  return (
    <div className="min-h-screen trubeaute-gradient">
      <Header />
      <main
        className="pt-32 pb-24 px-4 sm:px-6 max-w-6xl mx-auto"
        data-testid="affirming-resources-page"
      >
        {/* ── Hero ─────────────────────────────────────────────────── */}
        <section className="mb-10 max-w-3xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#2C2123] leading-tight mb-5">
            {t("resources.hero.title")}
          </h1>
          <p className="text-lg sm:text-xl font-serif italic text-[#5D4050] mb-4 leading-snug">
            {t("resources.hero.subtitle")}
          </p>
          <p className="text-base text-[#5C4B51] leading-relaxed mb-7">
            {t("resources.hero.body")}
          </p>

          {/* Highlight card */}
          <div
            className="rounded-2xl bg-gradient-to-br from-[#F5EFEB] to-[#FFFFFF] border border-[#EADDD7] p-6 sm:p-7 shadow-[0_8px_24px_rgba(155,106,108,0.08)]"
            data-testid="resources-highlight-card"
          >
            <div className="flex items-start gap-3">
              <Heart
                className="h-6 w-6 text-[#9B6A6C] mt-0.5 shrink-0"
                fill="currentColor"
              />
              <div>
                <p className="text-lg font-serif text-[#2C2123] mb-1.5">
                  {t("resources.hero.highlightTitle")}
                </p>
                <p className="text-sm text-[#5C4B51] leading-relaxed">
                  {t("resources.hero.highlightBody")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Search + Filters ────────────────────────────────────── */}
        <section className="mb-8" data-testid="resources-filters">
          <div className="relative mb-5">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9B6A6C]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("resources.filters.searchPlaceholder")}
              className="w-full bg-white border border-[#EADDD7] rounded-full pl-11 pr-5 py-3 text-sm text-[#2C2123] placeholder-[#A89094] focus:outline-none focus:border-[#D5A8A4] focus:shadow-[0_4px_14px_rgba(155,106,108,0.12)] transition-all"
              data-testid="resources-search-input"
              aria-label={t("resources.filters.searchAriaLabel")}
            />
          </div>

          <div className="mb-4">
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#9B6A6C] mb-2.5">
              {t("resources.filters.countryLabel")}
            </p>
            <div className="flex flex-wrap gap-2">
              <FilterChip
                active={country === "all"}
                label={t("resources.filters.allCountries")}
                onClick={() => setCountry("all")}
                testId="filter-country-all"
              />
              {COUNTRY_CODES.map((c) => (
                <FilterChip
                  key={c}
                  active={country === c}
                  label={`${COUNTRY_FLAGS[c]} ${t(`resources.country.${c}`)}`}
                  onClick={() => setCountry(c)}
                  testId={`filter-country-${c}`}
                />
              ))}
            </div>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#9B6A6C] mb-2.5">
              {t("resources.filters.categoryLabel")}
            </p>
            <div className="flex flex-wrap gap-2">
              <FilterChip
                active={category === "all"}
                label={t("resources.filters.allCategories")}
                onClick={() => setCategory("all")}
                testId="filter-category-all"
              />
              {CATEGORY_CODES.map((c) => (
                <FilterChip
                  key={c}
                  active={category === c}
                  label={t(`resources.category.${c}`)}
                  onClick={() => setCategory(c)}
                  testId={`filter-category-${c}`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* ── Results ─────────────────────────────────────────────── */}
        <section
          className="mb-16"
          data-testid="resources-results"
          aria-live="polite"
        >
          {grouped.length === 0 ? (
            <div
              className="rounded-2xl bg-white border border-dashed border-[#EADDD7] p-10 text-center"
              data-testid="resources-empty-state"
            >
              <p className="text-base text-[#5C4B51] mb-1">
                {t("resources.results.emptyTitle")}
              </p>
              <p className="text-sm text-[#9B6A6C]">
                {t("resources.results.emptyBody")}
              </p>
            </div>
          ) : (
            grouped.map((group) => (
              <div
                key={group.country}
                className="mb-10"
                data-testid={`resources-group-${group.country}`}
              >
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="text-2xl" aria-hidden="true">
                    {COUNTRY_FLAGS[group.country]}
                  </span>
                  <h2 className="text-2xl font-light text-[#2C2123]">
                    {t(`resources.country.${group.country}`)}
                  </h2>
                  <span className="text-xs text-[#A89094]">
                    {t("resources.results.countCount", {
                      count: group.rows.length,
                    })}
                  </span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.rows.map((r) => (
                    <ResourceCard
                      key={r.id}
                      resource={r}
                      descriptionText={resolveDescription(r, locale)}
                      categoryLabel={t(`resources.category.${r.category}`)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </section>

        {/* ── TruBeauté section ───────────────────────────────────── */}
        <section
          className="mb-12 rounded-3xl bg-gradient-to-br from-[#FFFFFF] to-[#F5EFEB] border border-[#EADDD7] p-8 sm:p-10"
          data-testid="resources-trubeaute-section"
        >
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-[#9B6A6C]" />
            <span className="text-xs uppercase tracking-[0.25em] text-[#9B6A6C]">
              {t("resources.tru.kicker")}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-light text-[#2C2123] mb-5 leading-tight">
            {t("resources.tru.headline")}
          </h2>
          <p className="text-base text-[#5C4B51] leading-relaxed mb-5">
            {t("resources.tru.lead")}
          </p>
          <ul className="space-y-2.5 mb-7">
            {["item1", "item2", "item3", "item4", "item5"].map((k) => (
              <li
                key={k}
                className="flex items-start gap-3 text-[15px] text-[#3C2F33]"
              >
                <span
                  className="text-[#9B6A6C] font-medium shrink-0"
                  aria-hidden="true"
                >
                  ✓
                </span>
                <span>{t(`resources.tru.${k}`)}</span>
              </li>
            ))}
          </ul>
          <Link
            to={user ? "/home" : "/login?intent=signup"}
            className="inline-flex items-center gap-2 text-sm text-white bg-[#9B6A6C] hover:bg-[#805557] rounded-full px-6 py-3 transition-all shadow-[0_4px_14px_rgba(155,106,108,0.3)] hover:-translate-y-0.5"
            data-testid="resources-cta-start"
          >
            <Sparkles className="h-4 w-4" />
            {t("resources.tru.cta")}
          </Link>
        </section>

        {/* ── Disclaimer ──────────────────────────────────────────── */}
        <section
          className="text-[13px] text-[#8A7D82] leading-relaxed border-t border-[#EADDD7] pt-6"
          data-testid="resources-disclaimer"
        >
          <p className="mb-2 font-medium text-[#5C4B51]">
            {t("resources.disclaimer.title")}
          </p>
          <p className="mb-2">{t("resources.disclaimer.line1")}</p>
          <p className="mb-2">{t("resources.disclaimer.line2")}</p>
          <p>{t("resources.disclaimer.line3")}</p>
        </section>
      </main>
    </div>
  );
};

export default AffirmingResources;
